type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends ((k: infer I) => void) ? Extract<U, I> : never;

type EventDefinitions<T> = {
    [TKey in keyof T]: [...arguments: unknown[]];
};

type EventHandler<TEventDef extends EventDefinitions<TEventDef>, TEventName extends keyof TEventDef> =
    TEventDef extends unknown ? (...args: UnionToIntersection<TEventDef[TEventName]>) => unknown : never;

type EventHandlerParameters<TEventDef extends EventDefinitions<TEventDef>, TEventName extends keyof TEventDef> =
    TEventDef extends unknown ? UnionToIntersection<TEventDef[TEventName]> : never;

/**
 * Node.jsのEventEmitter、jQueryのon/off/trigger等のようなタイプの
 * イベントハンドリングを行うためのユーティリティクラス。
 */
export abstract class InheritableEventEmitter<TEventDefs extends EventDefinitions<TEventDefs>> {
    /** イベントハンドラーをまとめたオブジェクト */
    #eventHandlers: { [TEventName in keyof TEventDefs]: EventHandler<TEventDefs, TEventName>[] };

    /**
     * コンストラクター。
     */
    constructor() {
        this.#eventHandlers = Object.create(null) as { [TEventName in keyof TEventDefs]: EventHandler<TEventDefs, TEventName>[] };
    }

    /**
     * 指定されたイベントを発火する。
     *
     * @param eventName イベント名
     * @param params パラメーター
     */
    protected emit<TEventName extends keyof TEventDefs>(eventName: TEventName, ...params: EventHandlerParameters<TEventDefs, TEventName>) {
        const eh = this.#eventHandlers[eventName];
        if (eh) {
            for (const h of eh) {
                h(...params);
            }
        }
    }

    /**
     * イベントハンドラーを登録する。
     *
     * @param eventName イベント名
     * @param eventHandler イベントハンドラー
     */
    on<TEventName extends keyof TEventDefs>(eventName: TEventName, eventHandler: EventHandler<TEventDefs, TEventName>) {
        const eh = this.#eventHandlers[eventName] ?? [];
        eh.push(eventHandler);
        this.#eventHandlers[eventName] = eh;
    }

    /**
     * イベントハンドラーの登録を解除する。
     *
     * @param eventName イベント名
     * @param eventHandler イベントハンドラー
     */
    off<TEventName extends keyof TEventDefs>(eventName: TEventName, eventHandler: EventHandler<TEventDefs, TEventName>) {
        this.#eventHandlers[eventName] = (this.#eventHandlers[eventName] ?? [])
            .filter((x) => x !== eventHandler);
    }
}
